import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import * as types from '../../constants/actionTypes';
import { actions as toolTipActions } from '../../actions/toolTipActions';
import { getDataTableSortFields, getSearchParamSortFields } from '../../helpers/sortHelper';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT } from '../../constants/constants';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import Icon from '../Common/Icon';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import ToolTipDrawer from './ToolTipDrawer';
import './ToolTips.scss';
import Moment from 'moment';
import EditorPlain from '../Common/EditorPlain/EditorPlain';
import { getNullableOnChangeValue } from '../../helpers/multiselect';

export class ToolTips extends React.Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            isLoading: false,
            isToolTipDrawerOpen: false,
            tooltips: [],
            selectedToolTip: null,
            totalCount: 0,

            // filters for the drawer - only loaded once when null
            routes: null,

            searchParams: {
                title: "",
                isActive: true,
                pageSize: DEFAULT_PAGE_SIZE,
                pageNumber: 1,
                sortBy: "title",
                sortDir: "asc",
            }
        }
    }

    componentDidMount = () => {
        this.search();
    };

    search = async () => {
        try {

            this.setState({ isLoading: true });
            const response = await this.props.toolTipActions.search(this.state.searchParams);
            if (response.type === types.SEARCH_TOOL_TIPS_SUCCESS) {
                this.setState({ tooltips: response.payload.results, totalCount: response.payload.totalCount });
            } else if (response.type === types.SEARCH_TOOL_TIPS_FAILURE) {
                this.setState({ tooltips: [], totalCount: 0 });
                Notification("error", "Error", response.payload.message);
            }

        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ tooltips: [], totalCount: 0 });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    loadFilters = async () => {
        try {
            const response = await this.props.toolTipActions.loadFilters();
            if (response.type === types.LOAD_TOOL_TIPS_FILTERS_SUCCESS) {
                this.setState({ routes: response.payload.routes });
            } else if (response.type === types.LOAD_TOOL_TIPS_FILTERS_FAILURE) {
                this.setState({ routes: null });
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ routes: [] });
        }
    }

    onSelectToolTip = (e) => {
        this.openDrawer(e.value);
    }

    onAddNew = () => {
        this.openDrawer(null);
    }

    openDrawer = async (rowData) => {
        // if routes is null, then load them up
        if (this.state.routes == null) {
            await this.loadFilters();
        }
        this.setState({ isToolTipDrawerOpen: true, selectedToolTip: rowData });
    }

    onSort = ({ sortField, sortOrder }) => {
        const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
        const newSearchParams = {
            ...this.state.searchParams,
            sortBy: sortBy,
            sortDir: sortDir
        };

        this.setState({ searchParams: newSearchParams }, this.search);
    }

    titleTemplate = (rowData) => {
        const jsonMessage = JSON.parse(rowData.title);
        return <EditorPlain
            className="title-message"
            jsonMessage={jsonMessage}
        />;
    }

    startDateTemplate = (rowData) => {
        if (rowData.startDate && rowData.startDate.trim().length > 0) {
            return Moment.utc(rowData.startDate).format(DATE_FORMAT);
        } else {
            return '';
        }
    }

    expirationDateTemplate = (rowData) => {
        if (rowData.expirationDate && rowData.expirationDate.trim().length > 0) {
            return Moment.utc(rowData.expirationDate).format(DATE_FORMAT);
        } else {
            return '';
        }
    }

    activeTemplate = (rowData) => {
        if (rowData.isActive) {
            return <div className="text-success text-center"><Icon name="check-circle" /></div>;
        } else {
            return <div className="text-danger text-center"><Icon name="times-circle" /></div>;
        }
    }

    onActiveFilterChange = (e) => {
        const selectedValue = getNullableOnChangeValue(e);

        this.setState({ searchParams: { ...this.state.searchParams, isActive: selectedValue } }, () => {
            this.search();
        });
    }

    filterChange = (e) => {
        let name = e.target.name;
        this.setState({ searchParams: { ...this.state.searchParams, [name]: e.target.value } });
    }

    onSearchBlur = () => {
        this.search();
    }

    keyUpFilter = (e) => {
        if (e.keyCode == 13) {
            this.search();
        }
    }

    onDrawerSave = () => {
        this.setState({ isToolTipDrawerOpen: false }, () => {
            this.search()
        });
    }

    onDrawerCancel = () => {
        this.setState({ isToolTipDrawerOpen: false });
    }

    render() {
        const { sortField, sortOrder } = getDataTableSortFields(this.state.searchParams);

        const activeOptions = [
            { label: "Yes", value: true },
            { label: "No", value: false },
            { label: "All", value: null }
        ];

        const activeFilter = (
            <Dropdown
                name="isActive"
                className="ui-column-filter w-100"
                value={this.state.searchParams.isActive}
                style={{ textAlign: 'center', width: '76px' }}
                options={activeOptions}
                optionLabel="label"
                optionValue="value"
                onChange={this.onActiveFilterChange}
                placeholder="Select an option"
            />
        );

        const titleFilterElement =
            <input type="text" name="title" className="p-inputtext" placeholder="Filter by Title" size="50" onBlur={this.onSearchBlur} onChange={this.filterChange} onKeyUp={this.keyUpFilter} value={this.state.searchParams.title} />
            ;

        return (
            <Fragment>
                <div className="tooltips-page">
                    <div className="table-header">
                        <span className="title">Tool Tips</span>
                        <div className="add-new-button">
                            <button type="button" className="btn btn-primary" onClick={this.onAddNew} alt="close popup"><span className="me-2">Add New</span><Icon name="plus-circle" /></button>
                        </div>
                    </div>

                    <LoadingIndicator isLoading={this.state.isLoading} hideWhileLoading={true}>
                        <div className="list-results-table">
                            <DataTable
                                filterDisplay="row"
                                value={this.state.tooltips}
                                className="p-datatable-sm p-datatable-striped"
                                selectionMode="single"
                                onSelectionChange={this.onSelectToolTip}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSort={this.onSort}>
                                <Column field="title" filter={true} filterElement={titleFilterElement} header="Title" sortable={true} />
                                <Column field="startDate" header="Start Date" body={this.startDateTemplate} style={{ width: '200px' }} sortable={true} />
                                <Column field="expirationDate" header="Expriation Date" body={this.expirationDateTemplate} style={{ width: '200px' }} sortable={true} />
                                <Column field="routeName" header="Route Name" style={{ width: '200px' }} />
                                <Column field="isActive" header="Active" body={this.activeTemplate} style={{ width: '120px' }} filter={true} filterElement={activeFilter} sortable={true} />
                            </DataTable>
                            {
                                this.state.totalCount > this.state.searchParams.pageSize &&
                                <Paginator
                                    rows={this.state.searchParams.pageSize}
                                    totalRecords={this.state.totalCount}
                                    first={(this.state.searchParams.pageNumber - 1) * this.state.searchParams.pageSize}
                                    onPageChange={this.onPageChange} />
                            }
                            <div className="results-count">Total records: {this.state.totalCount}</div>
                        </div>
                    </LoadingIndicator>

                </div>
                {this.state.isToolTipDrawerOpen &&
                    <ToolTipDrawer
                        toolTip={this.state.selectedToolTip}
                        routes={this.state.routes}
                        onSave={this.onDrawerSave}
                        onCancel={this.onDrawerCancel}
                    />
                }
            </Fragment>
        )
    }
}

ToolTips.propTypes = {
    windowWidth: PropTypes.number,
    toolTipActions: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
    return {
        toolTipActions: bindActionCreators(toolTipActions, dispatch)
    }
}

export default connect(null, mapDispatchToProps)(ToolTips);
